'use client';
import React, { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useConfig } from '@/contexts/ConfigContext';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setDarkMode, setSidenavMinimized, setSidenavVisible } from '@/lib/slices/nav-slice';
import { setActiveView, setEffectiveRole, setisLoggedIn, setRole } from '@/lib/slices/login-slice';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import ProgressNav from './ProgressNav';
import BrandLogo from './BrandLogo';
import Link from 'next/link';
import Button from './ui/Button';
import { SunIcon, MoonIcon } from '@/assets/ui/theme';
import { ChevronDownIcon } from '@/assets/icons/chevron';
import { LifeBuoy } from 'lucide-react';
import { SideListIcon } from '@/assets/icons';
import { ExitIcon } from '@/assets/nav';

import { CourseId } from 'course-types';

import {
  NavItem,
  NavItemHideList,
  NavItemMap,
  filterNavItemsByPermissions,
} from '@/services/nav/nav-item-map';
import FeatureTrackingService from '@/services/analytics/feature-tracking-service';
import SiteThemCacheService from '@/services/storage/site-theme-cache-service';
import AuthService from '@/services/auth/auth-service';
import EffectiveRoleService from '@/services/auth/effective-role-service';
import ProfileService from '@/services/profile/profile-service';

const logoClassName = (isMobile: boolean): string =>
  `${isMobile ? 'w-32 mt-4' : 'w-44 mt-10'} mx-auto self-center flex`;

function SideNav(): React.JSX.Element | null {
  const config = useConfig()?.config ?? null;
  const router = useRouter();
  const pathname = usePathname();
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const {
    status: loggedIn,
    role: userRole,
    effectiveRole,
    first_name,
    last_name,
    email,
  } = useAppSelector((state) => state.isLoggedIn);
  const name = `${first_name} ${last_name}`;
  const initials = `${first_name?.[0] ?? ''}${last_name?.[0] ?? ''}`.toUpperCase();

  const visible = useAppSelector(() => store.getState().navigation.visible);
  const hidden = useAppSelector(() => store.getState().navigation.minimized);
  const darkMode: boolean = useAppSelector(() => store.getState().navigation.darkMode);
  const overlayOpen: boolean = useAppSelector(() => store.getState().navigation.overlayOpen);
  const activeStudentCourse = useAppSelector(() => store.getState().course.activeCourseData);
  const currentActivity = useAppSelector(() => store.getState().activity);

  const [themeCached, setThemeCached] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(name);
  const [displayInitials, setDisplayInitials] = useState(initials);
  const isActiveRoute = (route: string): boolean => pathname === `${route}`;
  const resolvedRoleType = EffectiveRoleService.resolveRoleType(effectiveRole, userRole);
  const activeView = useAppSelector((state) => state.isLoggedIn.activeView);
  const navRoleKey =
    resolvedRoleType === 'student' || activeView === 'student'
      ? 'student'
      : activeView === 'instructor'
        ? 'instructor'
        : activeView === 'admin'
          ? 'enterprise_admin'
          : (userRole as string) === 'enterprise_admin'
            ? 'enterprise_admin'
            : 'admin';
  const isStudentPortal = resolvedRoleType === 'student';
  const Logo = config?.logo;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const syncProfileData = () => {
      const profile = ProfileService.getProfileCookie();

      const nextPhoto =
        typeof profile?.profile_photo === 'string' && profile.profile_photo.trim().length > 0
          ? profile.profile_photo
          : null;
      setProfilePhoto(nextPhoto);

      const cookieFirst = profile?.first_name?.trim() || first_name || '';
      const cookieMiddle = profile?.middle_name?.trim() || '';
      const cookieLast = profile?.last_name?.trim() || last_name || '';
      const nextName = [cookieFirst, cookieMiddle, cookieLast].filter(Boolean).join(' ');
      const nextInitials = `${cookieFirst?.[0] ?? ''}${cookieLast?.[0] ?? ''}`.toUpperCase();

      if (nextName) setDisplayName(nextName);
      if (nextInitials) setDisplayInitials(nextInitials);
    };

    syncProfileData();
    window.addEventListener('profile-updated', syncProfileData);

    return () => {
      window.removeEventListener('profile-updated', syncProfileData);
    };
  }, [pathname, loggedIn, first_name, last_name]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!loggedIn || isActiveRoute('')) {
      dispatch(setSidenavVisible(false));
      dispatch(setSidenavMinimized(true));
    } else {
      dispatch(setSidenavMinimized(false));
    }
  }, [loggedIn, pathname]);

  // Close mobile sidenav when route changes
  useEffect(() => {
    if (isMobile && visible) {
      dispatch(setSidenavVisible(false));
    }
  }, [pathname, isMobile]);

  // Prevent body scroll when mobile sidenav is open
  useEffect(() => {
    if (isMobile && visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, visible]);

  // Set color theme for site
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark');
      document.documentElement.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
      document.documentElement.classList.remove('dark');
    }
    if (themeCached) SiteThemCacheService.setThemeCookie({ darkMode: darkMode });
  }, [darkMode]);

  useEffect(() => {
    if (loggedIn) {
      const theme = SiteThemCacheService.getThemeCookie();
      if (theme) {
        dispatch(setDarkMode(theme.darkMode));
      } else {
        SiteThemCacheService.setThemeCookie({ darkMode: darkMode });
      }
      setThemeCached(true);

      const savedAppearance = localStorage.getItem('userAppearanceSettings');
      if (savedAppearance) {
        try {
          const data = JSON.parse(savedAppearance);
          const root = document.documentElement;

          if (data?.highContrast) root.classList.add('a11y-high-contrast');
          else root.classList.remove('a11y-high-contrast');

          if (data?.screenReaderOptimized) root.classList.add('a11y-sr-optimized');
          else root.classList.remove('a11y-sr-optimized');

          if (data?.reduceMotion) root.classList.add('a11y-reduce-motion');
          else root.classList.remove('a11y-reduce-motion');
        } catch (e) {
          console.error('Failed to parse saved appearance settings', e);
        }
      }
    }
  }, [loggedIn]);

  const signOut = () => {
    const callback = () => {
      dispatch(setRole(''));
      dispatch(setEffectiveRole(null));
      dispatch(setActiveView(null));
      dispatch(setisLoggedIn(false));
      dispatch(setDarkMode(false));
    };
    AuthService.signOut(router, callback);
  };

  const toggleSidenav = () => {
    if (isMobile) {
      dispatch(setSidenavVisible(!visible));
      dispatch(setSidenavMinimized(false));
    } else {
      dispatch(setSidenavVisible(!visible));
    }
  };

  const closeMobileSidenav = () => {
    if (isMobile) {
      dispatch(setSidenavVisible(false));
    }
  };

  const handleThemeToggle = () => {
    const nextDarkMode = !darkMode;
    FeatureTrackingService.captureEvent('theme_toggled', {
      theme: nextDarkMode ? 'dark' : 'light',
    });
    dispatch(setDarkMode(nextDarkMode));

    try {
      const saved = localStorage.getItem('userAppearanceSettings');
      const existing = saved ? JSON.parse(saved) : {};
      localStorage.setItem(
        'userAppearanceSettings',
        JSON.stringify({ ...existing, theme: nextDarkMode ? 'dark' : 'light' }),
      );
    } catch (e) {
      console.error('Failed to sync theme to appearance settings', e);
    }
  };

  const handleSupportClick = () => {
    FeatureTrackingService.captureEvent('support_page_opened', {
      source_page: pathname,
    });
  };

  const onSideNavItemClick = (item: NavItem) => {
    if (item.subItems) {
      toggleDropdown(item.name);
      return;
    }
    if (item.name === 'Institute Overview') {
      FeatureTrackingService.captureEvent('overview_navigation_clicked', {
        destination: 'institute_overview',
      });
    }
    router.push(item.route);
    // Close mobile sidenav after navigation
    if (isMobile) {
      dispatch(setSidenavVisible(false));
    }
  };

  const toggleDropdown = (name: string) => {
    if (!visible) {
      toggleSidenav();
    }
    setOpenDropdown((prevDropdown) => (prevDropdown === name ? null : name));
  };

  const itemDisabled = (item: NavItem): boolean => {
    if (item.route.endsWith('/student/course') && activeStudentCourse.id == ('' as CourseId)) {
      return true;
    }
    return false;
  };

  const getNavItems = () => {
    if (userRole === '') return [];

    let roleItems = filterNavItemsByPermissions(NavItemMap[navRoleKey] ?? [], effectiveRole);
    if (activeView === 'admin') {
      roleItems = roleItems.filter((item) => item.name !== 'Home');
    }
    return roleItems.map((item) => {
      if (!NavItemHideList[navRoleKey].includes(item.name)) {
        const isParentActive =
          isActiveRoute(`${item.route}/`) ||
          (item.subItems && item.subItems.some((sub) => isActiveRoute(`${sub.route}/`)));
        const isDropdownOpen = openDropdown === item.name;
        return (
          <React.Fragment key={item.name}>
            <li
              onClick={itemDisabled(item) ? () => {} : () => onSideNavItemClick(item)}
              className={`w-full group flex flex-row dropdown-toggle ${visible ? 'my-1 px-2 gap-2 clickable' : 'clickable-icon'}${itemDisabled(item) ? '-disabled' : ''} ${isParentActive && !item.subItems ? 'active' : ''}`}
            >
              {item?.icon && <item.icon className={`w-6 h-6`} />}
              <span className={`text-inherit ${visible ? '' : 'hidden'}`}>{item.name}</span>
              {item.subItems && visible && (
                <div
                  className='ml-auto flex h-4 w-4 items-center justify-center transition-transform duration-200'
                  style={{
                    transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <ChevronDownIcon className='w-4 h-4 text-text_gray_400' />
                </div>
              )}
            </li>
            {visible && getNavSubItems(item)}
          </React.Fragment>
        );
      }
      return null;
    });
  };

  const getNavSubItems = (item: NavItem) => {
    if (openDropdown !== item.name || !item.subItems) {
      return null;
    }

    return (
      <ul>
        {item.subItems.map((sub) => {
          if (!NavItemHideList[navRoleKey].includes(sub.name)) {
            return (
              <li
                key={sub.name}
                onClick={() => {
                  // Reset course data when clicking "All Courses" in sidebar
                  if (sub.route === '/admin/courses') {
                    dispatch(
                      setActiveCourseData({
                        id: '' as CourseId,
                        name: '',
                        units: {} as Record<string, any>,
                      }),
                    );
                  }
                  if (sub.name === 'Overview') {
                    FeatureTrackingService.captureEvent('overview_navigation_clicked', {
                      destination: 'reports_overview',
                    });
                  }
                  router.push(sub.route);
                  if (isMobile) {
                    dispatch(setSidenavVisible(false));
                  }
                }}
                className={`clickable my-2 px-2 ml-4 truncate whitespace-nowrap font-normal ${isActiveRoute(`${sub.route}/`) ? 'active' : ''}`}
              >
                {sub.name}
              </li>
            );
          }
          return null;
        })}
      </ul>
    );
  };

  const sideNavToggle = () => {
    return (
      <a
        onClick={toggleSidenav}
        className={`text-text_gray_500 hover:text-text_gray_700 ${visible ? '' : 'self-center'}`}
      >
        <SideListIcon className='flex justify-center items-center' />
      </a>
    );
  };

  const renderLogOut = () => {
    return (
      <>
        <ul className='mb-2 px-3'>
          {!isStudentPortal && (
            <Link
              href='https://assistiv.site/'
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Open Assistiv Support documentation in a new tab'
              className='flex group clickable items-center gap-2 w-full h-fit text-inherit no-underline'
              onClick={handleSupportClick}
            >
              <li
                className={`w-full group flex flex-row ${visible ? 'my-1 px-2 gap-2 clickable' : 'clickable-icon'}`}
              >
                <LifeBuoy className='w-6 h-6' strokeWidth={1.5} />
                <span className={`text-inherit ${visible ? '' : 'hidden'}`}>Support</span>
              </li>
            </Link>
          )}
          <li
            onClick={signOut}
            className={`w-full group flex flex-row dropdown-toggle ${visible ? 'my-2 px-2 gap-2 clickable' : 'clickable-icon'}`}
          >
            <ExitIcon className={`w-6 h-6`} />
            <span className={`text-inherit ${visible ? '' : 'hidden'}`}>{'Log Out'}</span>
          </li>
        </ul>
      </>
    );
  };

  const renderProfileLogout = () => {
    if (!isStudentPortal) {
      return (
        <div className={`${visible ? 'px-4 pt-1 pb-8' : 'px-3'}`}>
          {visible && (
            <div className='py-1' role='separator'>
              <hr className='w-full border-gray-200 dark:border-[#333741]' />
            </div>
          )}
          <ul className={`${visible ? 'flex flex-col px-2' : 'flex flex-col items-center gap-3'}`}>
            <Link
              href='https://assistiv.site/'
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Open Assistiv Support documentation in a new tab'
              className='flex items-center justify-center text-inherit no-underline w-full'
              onClick={handleSupportClick}
            >
              <li
                className={`group flex items-center justify-center clickable ${visible ? 'w-full flex-row px-2 my-1 gap-2' : 'w-10 h-10'}`}
              >
                <LifeBuoy
                  className='w-6 h-6 flex-shrink-0 relative -left-1 top-1'
                  strokeWidth={1.5}
                />
                {visible && <span className='text-inherit'>Support</span>}
              </li>
            </Link>
            <div
              className={`${visible ? 'flex flex-row px-2 items-center' : 'flex flex-col items-center gap-3'}`}
            >
              <Link
                href='/admin/profile'
                title='Profile'
                aria-label={`View profile for ${displayName}`}
                className={`no-underline text-inherit rounded ${visible ? 'flex-1 min-w-0' : 'size-10'} hover:opacity-100`}
              >
                <li
                  className={`group flex items-center ${visible ? 'flex-row gap-3' : 'clickable-icon w-10'} rounded [&.clickable-icon]:my-0`}
                >
                  <div
                    className={`${visible ? 'flex-shrink-0 size-10' : 'size-6'} rounded-full !bg-[#EFF8FF] dark:!bg-[#0B0F19] flex items-center justify-center transform transition-[width,height] duration-300 ease-in-out`}
                    aria-hidden='true'
                  >
                    {profilePhoto ? (
                      <img
                        src={profilePhoto}
                        alt={`${displayName} profile photo`}
                        className='h-full w-full rounded-full object-cover'
                      />
                    ) : (
                      <span
                        className={`${visible ? 'text-xl' : 'text-xs'} font-medium leading-none !text-[#4A6CF7]`}
                      >
                        {displayInitials}
                      </span>
                    )}
                  </div>
                  <div className={`${visible ? 'flex flex-col min-w-0' : 'hidden'}`}>
                    <span className='font-semibold text-sm truncate'>{displayName}</span>
                    <span className='text-xs text-gray-500 truncate'>{email}</span>
                  </div>
                </li>
              </Link>
              <Button
                onClick={signOut}
                title='Log out'
                aria-label='Log out of your account'
                className={`${visible ? 'h-6 w-6 min-w-6' : 'h-10 w-10 mb-4'} relative rounded hover:opacity-100 hover:text-failure`}
                text
              >
                <li
                  className={`w-fit absolute ${visible ? 'dropdown-toggle top-[-12px] right-[-12px] p-3 cursor-pointer hover:translate-x-1 transform' : 'clickable-icon top-0 right-0'} rounded [&.clickable-icon]:my-0`}
                  style={{
                    transition:
                      'top 300ms ease-in-out, right 300ms ease-in-out, transform 150ms ease-out',
                  }}
                >
                  <ExitIcon
                    className={`${visible ? 'rotate-180' : ''} size-6 transform transition-transform duration-300 ease-in-out`}
                    aria-hidden='true'
                  />
                  <span className='sr-only'>Log Out</span>
                </li>
              </Button>
            </div>
          </ul>
        </div>
      );
    }
    return (
      <div className={` ${visible ? 'px-4 py-8' : 'px-3'}`}>
        {visible && (
          <div className='py-6' role='separator'>
            <hr className='w-full border-gray-200 dark:border-[#333741]' />
          </div>
        )}
        <ul
          className={`${visible ? 'flex flex-row px-2 items-center' : 'flex flex-col items-center gap-3'}`}
        >
          <Link
            href='/student/profile'
            title={`Profile`}
            aria-label={`View profile for ${displayName}`}
            className={`no-underline text-inherit rounded ${visible ? 'flex-1 min-w-0' : 'size-10'} hover:opacity-100`}
          >
            <li
              className={`group flex items-center ${visible ? 'flex-row gap-3' : 'clickable-icon w-10'} rounded [&.clickable-icon]:my-0`}
            >
              <div
                className={`${visible ? 'flex-shrink-0 size-10' : 'size-6'} rounded-full !bg-[#EFF8FF] dark:!bg-[#0B0F19] flex items-center justify-center transform transition-[width,height] duration-300 ease-in-out`}
                aria-hidden='true'
              >
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt={`${displayName} profile photo`}
                    className='h-full w-full rounded-full object-cover'
                  />
                ) : (
                  <span
                    className={`${visible ? 'text-xl' : 'text-xs'} font-medium leading-none !text-[#4A6CF7]`}
                  >
                    {displayInitials}
                  </span>
                )}
              </div>
              <div className={`${visible ? 'flex flex-col min-w-0' : 'hidden'}`}>
                <span className='font-semibold text-sm truncate'>{displayName}</span>
                <span className='text-xs text-gray-500 truncate'>{email}</span>
              </div>
            </li>
          </Link>
          <Button
            onClick={signOut}
            title='Log out'
            aria-label='Log out of your account'
            className={`${visible ? 'h-6 w-6 min-w-6' : 'h-10 w-10 mb-4'} relative rounded hover:opacity-100 hover:text-failure`}
            text
          >
            <li
              className={`w-fit absolute ${visible ? 'dropdown-toggle top-[-12px] right-[-12px] p-3 cursor-pointer hover:translate-x-1 transform ' : 'clickable-icon top-0 right-0'} rounded [&.clickable-icon]:my-0`}
              style={{
                transition:
                  'top 300ms ease-in-out, right 300ms ease-in-out, transform 150ms ease-out',
              }}
            >
              <ExitIcon
                className={`${visible ? 'rotate-180 ' : ''} size-6 transform transition-transform duration-300 ease-in-out`}
                aria-hidden='true'
              />
              <span className='sr-only'>Log Out</span>
            </li>
          </Button>
        </ul>
      </div>
    );
  };

  if (!loggedIn) return null;

  return (
    <>
      {/* Mobile Modal Overlay */}
      {isMobile && visible && (
        <div
          className='fixed inset-0 bg-background_gray_50 z-40 h-[calc(100vh)]'
          onClick={closeMobileSidenav}
        />
      )}

      <div
        className={`sidebar 
          flex-none h-full transition-all duration-300 ease-in-out
          ${isMobile && visible ? 'fixed inset-4 w-auto max-w-[260px] mx-auto h-[calc(95vh)]' : 'fixed top-0 left-0 h-full'}
          z-50 max-h-screen overflow-y-auto
          ${visible ? (isMobile ? '' : activeStudentCourse.id ? 'max-w-xs w-[280px]' : 'max-w-xs w-[280px]') : isMobile ? 'w-0' : 'w-16'}
          ${visible ? 'visible' : ''}
          ${isMobile ? '' : 'border-r-2 border-r-border_sidenav'}
          ${isMobile && visible ? 'bg-surface_white rounded-lg shadow-lg' : 'bg-sidenav_white'}`}
      >
        {currentActivity.id && isStudentPortal ? (
          <nav className={`flex flex-col h-full justify-between ${visible ? '' : 'pt-4'}`}>
            <div className='flex flex-col px-3 flex-1 min-h-0'>
              {!visible && sideNavToggle()}
              {visible && <ProgressNav onClick={toggleSidenav} />}
            </div>
            {renderProfileLogout()}
          </nav>
        ) : (
          <nav className={`flex flex-col h-full w-full overflow-hidden`}>
            <div className={`my-4 flex flex-row justify-between px-5 flex-shrink-0`}>
              {sideNavToggle()}
              {visible && (
                <div className='flex items-center gap-3'>
                  <div
                    className='flex flex-row text-text_gray_500 hover:cursor-pointer hover:text-text_gray_700'
                    onClick={handleThemeToggle}
                  >
                    {!darkMode ? <SunIcon className='w-6 h-6' /> : <MoonIcon className='w-6 h-6' />}
                  </div>
                  {/* Mobile close button */}
                  {isMobile && (
                    <button
                      onClick={closeMobileSidenav}
                      className='p-1 rounded-md hover:bg-gray-100 transition-colors'
                      aria-label='Close navigation menu'
                    >
                      <svg
                        className='w-5 h-5'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M6 18L18 6M6 6l12 12'
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
            {visible && (
              <div className='flex-shrink-0 flex items-center justify-center px-3'>
                <BrandLogo
                  logo={Logo}
                  brandName={config?.name ?? 'Assistiv'}
                  className={`${logoClassName(isMobile)} px-3 text-[var(--header-text-primary)] [&>svg]:h-auto [&>svg]:w-full`}
                />
              </div>
            )}
            <ul className='mt-4 px-3 flex-1 overflow-y-auto'>{getNavItems()}</ul>
            <div className='flex-shrink-0'>{renderProfileLogout()}</div>
          </nav>
        )}
      </div>
    </>
  );
}

export default SideNav;
